import { useState, useEffect, useCallback, useMemo } from "react"
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/use-auth"
import { usePermissions } from "@/hooks/use-permissions"
import type { PermissionModule } from "@/lib/permissions"
import { useCurrentOrg, orgPath } from "@/hooks/use-current-org"
import { OrgSwitcher } from "@/components/OrgSwitcher"
import { TenantContextBanner } from "@/components/TenantContextBanner"
import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { ScrollArea } from "@/components/ui/scroll-area"
import * as O from "fp-ts/Option"
import { cn } from "@/lib/utils"
import { activeSidebarLink } from "@/lib/sidebar-active-link"
import { CommandPalette } from "@/components/CommandPalette"
import { AIChatWidget } from "@/components/AIChatWidget"
import { SidebarOnboardingWidget } from "@/components/onboarding/SidebarOnboardingWidget"
import { TenantSwitcher } from "@/components/onboarding/TenantSwitcher"
import { EntityCompletenessProvider } from "@/components/onboarding/EntityCompletenessContext"
import { EntityCompletenessSlot } from "@/components/onboarding/EntityCompletenessSlot"
import { TenantTerminationBanner } from "@/components/TenantTerminationBanner"
import {
  LayoutDashboard,
  Users,
  Building2,
  UserCircle,
  Target,
  GraduationCap,
  BookOpen,
  Calendar,
  CalendarDays,
  ClipboardList,
  CheckSquare,
  Award,
  AlertTriangle,
  TrendingUp,
  MapPin,
  FileText,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Receipt,
  FileSignature,
  CreditCard,
  ClipboardCheck,
  Eye,
  BarChart3,
  FolderOpen,
  BookMarked,
  Handshake,
  Workflow,
  FilePlus2,
  MessageSquare,
  Mail,
  FileBarChart,
  Clock,
  Download,
  Shield,
  ScrollText,
  Scale,
  Search,
  ShieldCheck,
  Globe,
  Accessibility,
  Gauge,
  HelpCircle,
  UserX,
  ListTodo,
} from "lucide-react"

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  children?: NavItem[]
  /** Explicit RBAC module override for permission gating. When omitted, the
   * module is derived from the href's first path segment (`moduleForNav`).
   * Set it only where the href segment does not match the gating intent — e.g.
   * `/settings/opco-rules` is org-admin (`settings`) while `/settings` (profile)
   * stays open to every role. */
  module?: PermissionModule
}

const navigation: NavItem[] = [
  {
    title: "Tableau de bord",
    href: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Tâches",
    href: "/tasks",
    icon: ListTodo,
  },
  {
    title: "CRM",
    href: "/crm",
    icon: Users,
    children: [
      { title: "Comptes", href: "/crm/accounts", icon: Building2 },
      { title: "Contacts", href: "/crm/contacts", icon: UserCircle },
      { title: "Opportunités", href: "/crm/opportunities", icon: Target },
      { title: "Analyse des besoins", href: "/crm/needs-assessments", icon: Search },
    ],
  },
  {
    title: "Formation",
    href: "/training",
    icon: GraduationCap,
    children: [
      { title: "Programmes", href: "/training/programs", icon: BookOpen },
      { title: "Sessions", href: "/training/sessions", icon: Calendar },
      { title: "Calendrier", href: "/training/calendar", icon: CalendarDays },
      { title: "Inscriptions", href: "/training/registrations", icon: ClipboardList },
      { title: "Pipeline", href: "/training/enrollment-pipeline", icon: Workflow },
      { title: "Émargement", href: "/training/attendance", icon: CheckSquare },
      { title: "Référentiels", href: "/admin/competency-frameworks", icon: BookOpen, module: "training" },
      { title: "Jurys", href: "/training/juries", icon: Scale, module: "jury" },
      { title: "Blocs de compétences", href: "/training/competency-blocks", icon: ClipboardList },
      { title: "VAE", href: "/training/vae", icon: Award },
      { title: "Abandons", href: "/training/dropouts", icon: UserX },
    ],
  },
  {
    title: "Commercial",
    href: "/commercial",
    icon: Receipt,
    children: [
      { title: "Tableau de bord", href: "/commercial/dashboard", icon: LayoutDashboard },
      { title: "Devis", href: "/commercial/quotes", icon: FileText },
      { title: "Factures", href: "/commercial/invoices", icon: CreditCard },
      { title: "Contrats", href: "/commercial/contracts", icon: FileSignature },
      { title: "Annuaire OPCO", href: "/commercial/opco", icon: BookMarked },
      { title: "Dossiers OPCO", href: "/commercial/opco-files", icon: FolderOpen },
    ],
  },
  {
    title: "RH",
    href: "/hr",
    icon: Users,
    children: [
      { title: "Personnel", href: "/hr/staff", icon: UserCircle },
      { title: "Formateurs", href: "/hr/instructors", icon: GraduationCap },
      { title: "Auto-vérification", href: "/hr/self-verification", icon: ClipboardCheck },
      { title: "Éval. Compétences", href: "/hr/competency-evaluations", icon: ClipboardList },
      { title: "Entretiens pro", href: "/hr/professional-interviews", icon: Handshake },
      { title: "Sous-traitants", href: "/hr/vendors", icon: Building2 },
      { title: "Contrats ST", href: "/hr/vendor-contracts", icon: FileText },
    ],
  },
  {
    title: "Qualiopi",
    href: "/qualiopi",
    icon: Award,
    children: [
      { title: "Indicateurs", href: "/qualiopi/indicators", icon: CheckSquare },
      { title: "Évaluations", href: "/qualiopi/evaluations", icon: ClipboardCheck },
      { title: "Réclamations", href: "/qualiopi/complaints", icon: AlertTriangle },
      { title: "Améliorations", href: "/qualiopi/improvements", icon: TrendingUp },
      { title: "Lieux", href: "/qualiopi/venues", icon: MapPin, module: "training" },
      { title: "Veille", href: "/qualiopi/watch", icon: Eye },
      { title: "Enquêtes", href: "/qualiopi/surveys", icon: ClipboardList },
      { title: "KPIs Marketing", href: "/qualiopi/marketing-kpis", icon: BarChart3 },
      { title: "Simulateur", href: "/qualiopi/simulator", icon: Gauge },
      { title: "Organismes handicap", href: "/admin/disability-organizations", icon: Accessibility, module: "crm" },
    ],
  },
  {
    title: "Documents",
    href: "/documents",
    icon: FileText,
    children: [
      { title: "Tous les documents", href: "/documents", icon: FolderOpen },
      { title: "Générer", href: "/documents/generate", icon: FilePlus2 },
    ],
  },
  {
    title: "Messages",
    href: "/messages",
    icon: MessageSquare,
  },
  {
    title: "Rapports",
    href: "/reports",
    icon: FileBarChart,
    children: [
      { title: "BPF", href: "/reports/bpf", icon: FileText },
      { title: "Statistiques", href: "/reports/statistics", icon: BarChart3 },
    ],
  },
  {
    title: "Administration",
    href: "/admin",
    icon: Shield,
    children: [
      { title: "Modèles email", href: "/admin/email-templates", icon: Mail, module: "templates" },
      { title: "Modèles documents", href: "/admin/document-templates", icon: FileText, module: "templates" },
      { title: "Tâches planifiées", href: "/admin/scheduled-jobs", icon: Clock },
      { title: "Exports", href: "/admin/exports", icon: Download, module: "reports" },
      { title: "Journal d'audit", href: "/admin/audit-log", icon: ScrollText },
      { title: "Journal des emails", href: "/admin/email-logs", icon: Mail },
      { title: "File d'envoi", href: "/admin/email-outbox", icon: Mail, module: "settings" },
      { title: "KPIs Marketing", href: "/admin/marketing-kpis", icon: BarChart3, module: "reports" },
      { title: "Validation CPF", href: "/admin/cpf-validation", icon: ShieldCheck },
      { title: "Catalogue public", href: "/admin/public-catalog", icon: Globe },
    ],
  },
  {
    title: "Paramètres",
    href: "/settings",
    icon: Settings,
    children: [
      { title: "Organisme et profil", href: "/settings", icon: Settings },
      { title: "Règles OPCO", href: "/settings/opco-rules", icon: ShieldCheck, module: "settings" },
    ],
  },
  {
    title: "Aide",
    href: "/help",
    icon: HelpCircle,
  },
]

const navCategoryColors: Record<string, { text: string; border: string; bg: string }> = {
  "/dashboard": { text: "text-primary", border: "border-primary", bg: "bg-primary/5" },
  "/crm": { text: "text-blue-500 dark:text-blue-400", border: "border-blue-500", bg: "bg-blue-500/5" },
  "/training": { text: "text-violet-500 dark:text-violet-400", border: "border-violet-500", bg: "bg-violet-500/5" },
  "/commercial": { text: "text-amber-500 dark:text-amber-400", border: "border-amber-500", bg: "bg-amber-500/5" },
  "/hr": { text: "text-rose-500 dark:text-rose-400", border: "border-rose-500", bg: "bg-rose-500/5" },
  "/qualiopi": { text: "text-emerald-500 dark:text-emerald-400", border: "border-emerald-500", bg: "bg-emerald-500/5" },
  "/documents": { text: "text-orange-500 dark:text-orange-400", border: "border-orange-500", bg: "bg-orange-500/5" },
  "/messages": { text: "text-sky-500 dark:text-sky-400", border: "border-sky-500", bg: "bg-sky-500/5" },
  "/reports": { text: "text-indigo-500 dark:text-indigo-400", border: "border-indigo-500", bg: "bg-indigo-500/5" },
  "/admin": { text: "text-slate-500 dark:text-slate-400", border: "border-slate-500", bg: "bg-slate-500/5" },
  "/settings": { text: "text-muted-foreground", border: "border-primary", bg: "bg-primary/5" },
  "/help": { text: "text-muted-foreground", border: "border-primary", bg: "bg-primary/5" },
}
const defaultNavColor = { text: "text-muted-foreground", border: "border-primary", bg: "bg-primary/5" }

function NavItemComponent({ item, isCollapsed = false, slug, colorSet = defaultNavColor }: { item: NavItem; isCollapsed?: boolean; slug: string; colorSet?: { text: string; border: string; bg: string } }) {
  const location = useLocation()
  const prefixedHref = orgPath(slug, item.href)
  const isActive = location.pathname === prefixedHref || location.pathname.startsWith(prefixedHref + "/")
  const hasActiveChild = item.children?.some(
    (child) => {
      const childHref = orgPath(slug, child.href)
      return location.pathname === childHref || location.pathname.startsWith(childHref + "/")
    }
  ) ?? false
  const [isOpen, setIsOpen] = useState(isActive || hasActiveChild)

  useEffect(() => {
    if (isActive || hasActiveChild) {
      setIsOpen(true)
    }
  }, [isActive, hasActiveChild])
  const hasChildren = item.children && item.children.length > 0
  const itemSlug = item.href.replace(/^\//, "").replace(/\//g, "-")

  if (hasChildren) {
    return (
      <div className="space-y-0.5">
        <button
          type="button"
          data-test={`sidebar-group-${itemSlug}`}
          className={cn(
            "flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
            (isActive || hasActiveChild) && "bg-accent text-foreground"
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <item.icon className={cn("mr-3 h-4 w-4 flex-shrink-0", colorSet.text)} />
          {!isCollapsed && (
            <>
              <span className="flex-1 text-left">{item.title}</span>
              <ChevronDown
                className={cn("h-3.5 w-3.5 text-muted-foreground/60 transition-transform duration-200", isOpen && "rotate-180")}
              />
            </>
          )}
        </button>
        {isOpen && !isCollapsed && item.children && (
          <div className="space-y-0.5">
            {item.children.map((child, _index, siblings) => {
              const childHref = orgPath(slug, child.href)
              const activeHref = activeSidebarLink(location.pathname, siblings.map((sibling) => orgPath(slug, sibling.href)))
              const isChildActive = O.isSome(activeHref) && activeHref.value === childHref
              const childSlug = child.href.replace(/^\//, "").replace(/\//g, "-")
              return (
                <Link
                  key={child.href}
                  to={childHref}
                  data-test={`sidebar-link-${childSlug}`}
                  className={cn(
                    "flex items-center rounded-lg py-1.5 pl-9 pr-3 text-sm text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
                    isChildActive && `border-l-2 ${colorSet.border} ${colorSet.bg} font-medium text-foreground`
                  )}
                >
                  {child.title}
                </Link>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  return (
    <Link
      to={prefixedHref}
      data-test={`sidebar-link-${itemSlug}`}
      className={cn(
        "flex items-center rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground",
        isActive && "bg-accent text-foreground"
      )}
    >
      <item.icon className={cn("mr-3 h-4 w-4 flex-shrink-0", colorSet.text)} />
      {!isCollapsed && item.title}
    </Link>
  )
}

// Split navigation: main items vs bottom-pinned items (Paramètres, Aide)
const mainNavigation = navigation.filter((item) => item.href !== "/settings" && item.href !== "/help")
const bottomNavigation = navigation.filter((item) => item.href === "/settings" || item.href === "/help")

/** First path segment (`/crm/accounts` -> `crm`) mapped to its RBAC module.
 * Consistent with the route guards in `App.tsx` (both gate by path prefix).
 * Segments absent from this map (dashboard, tasks, help, settings) are never
 * permission-gated — they hold personal / always-available surfaces. */
const NAV_SEGMENT_TO_MODULE: Record<string, PermissionModule> = {
  crm: "crm",
  training: "training",
  commercial: "commercial",
  hr: "hr",
  qualiopi: "qualiopi",
  documents: "documents",
  messages: "messaging",
  admin: "admin",
  reports: "reports",
}

/** RBAC module gating a nav item: explicit `module` override wins, else derived
 * from the href's first segment. `undefined` = never gated (always visible). */
function moduleForNav(item: NavItem): PermissionModule | undefined {
  if (item.module !== undefined) {
    return item.module
  }
  const segment = item.href.split("/").filter(Boolean)[0]
  return segment === undefined ? undefined : NAV_SEGMENT_TO_MODULE[segment]
}

/** Keep only nav items the current user is allowed to open. A group survives if
 * at least one of its children survives (children are filtered by their own
 * module, so cross-module children like `/admin/...` nested under Formation are
 * dropped for a role lacking `admin`). Leaf items survive when their module is
 * undefined or readable. Recurses into children. */
function filterNavByPermission(
  items: NavItem[],
  canRead: (module: PermissionModule) => boolean,
): NavItem[] {
  const result: NavItem[] = []
  for (const item of items) {
    if (item.children !== undefined && item.children.length > 0) {
      const children = filterNavByPermission(item.children, canRead)
      if (children.length > 0) {
        result.push({ ...item, children })
      }
      continue
    }
    const module = moduleForNav(item)
    if (module === undefined || canRead(module)) {
      result.push(item)
    }
  }
  return result
}

function Sidebar({ className, slug }: { className?: string; slug: string }) {
  const { canRead } = usePermissions()
  const visibleMain = useMemo(() => filterNavByPermission(mainNavigation, canRead), [canRead])
  const visibleBottom = useMemo(() => filterNavByPermission(bottomNavigation, canRead), [canRead])
  return (
    <div className={cn("flex h-full flex-col", className)}>
      <div className="px-4 pb-2 pt-5">
        <h2 className="mb-3 px-1 text-base font-semibold tracking-tight text-foreground">Qualneo</h2>
        <div className="mb-4">
          <OrgSwitcher />
        </div>
        <div className="h-px bg-border/60" />
      </div>
      <EntityCompletenessSlot />
      <ScrollArea className="flex-1 scrollbar-thin">
        <div className="space-y-0.5 px-3 py-2">
          {visibleMain.map((item) => (
            <NavItemComponent key={item.href} item={item} slug={slug} colorSet={navCategoryColors[item.href] || defaultNavColor} />
          ))}
        </div>
      </ScrollArea>
      <SidebarOnboardingWidget />
      <div className="border-t border-border/60 px-3 py-2">
        <div className="space-y-0.5">
          {visibleBottom.map((item) => (
            <NavItemComponent key={item.href} item={item} slug={slug} colorSet={navCategoryColors[item.href] || defaultNavColor} />
          ))}
        </div>
      </div>
    </div>
  )
}

export function DashboardLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const orgSlug = useCurrentOrg()
  const [commandOpen, setCommandOpen] = useState(false)
  // Le panneau recouvre la page.
  // Il ne reste ouvert que sur sa page d'ouverture.
  // `location.key` change à chaque navigation.
  // Même vers la page déjà affichée, contrairement au chemin.
  const [mobileMenu, setMobileMenu] = useState({ open: false, key: location.key })
  const mobileMenuOpen = mobileMenu.open && mobileMenu.key === location.key
  const changeMobileMenu = useCallback(
    (open: boolean) => setMobileMenu({ open, key: location.key }),
    [location.key],
  )

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Cmd+K or Ctrl+K
    if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault()
      setCommandOpen((prev) => !prev)
      return
    }
    // "/" to open, except when focused on an input/textarea/contenteditable
    if (e.key === "/" && !commandOpen) {
      const tag = (e.target as HTMLElement).tagName
      const isEditable = (e.target as HTMLElement).isContentEditable
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || isEditable) return
      e.preventDefault()
      setCommandOpen(true)
    }
  }, [commandOpen])

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  function handleLogout() {
    logout().then(() => {
      navigate("/login")
    })
  }

  const userInitials = user
    ? `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
    : "U"

  return (
    <EntityCompletenessProvider>
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-card/80 shadow-soft backdrop-blur-xl">
        <div className="container flex h-16 items-center">
          {/* Mobile menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={changeMobileMenu}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0">
              <SheetTitle className="sr-only">Menu de navigation</SheetTitle>
              <SheetDescription className="sr-only">Navigation principale de l'application</SheetDescription>
              <Sidebar slug={orgSlug} />
            </SheetContent>
          </Sheet>

          {/* Logo */}
          <Link to={orgPath(orgSlug, "/dashboard")} className="mr-6 flex items-center space-x-2.5">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-emerald-600">
              <span className="text-sm font-bold text-primary-foreground">Q</span>
            </div>
            <span className="hidden font-semibold tracking-tight sm:inline-block">Qualneo</span>
          </Link>

          {/* Tenant switcher */}
          <TenantSwitcher />

          {/* Search trigger */}
          <button
            type="button"
            onClick={() => setCommandOpen(true)}
            className="hidden w-96 items-center gap-2 rounded-xl bg-muted/50 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-accent/50 md:flex"
            aria-label="Rechercher"
            data-test="global-search-trigger"
          >
            <Search className="h-4 w-4" />
            <span>Rechercher...</span>
            <kbd className="pointer-events-none ml-2 rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] font-medium text-muted-foreground/70">
              ⌘K
            </kbd>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8 ring-2 ring-border transition-all hover:ring-primary/40">
                  <AvatarFallback className="bg-primary/10 text-primary font-medium">{userInitials}</AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to={orgPath(orgSlug, "/settings/profile")}>
                  <UserCircle className="mr-2 h-4 w-4" />
                  Profil
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={orgPath(orgSlug, "/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Paramètres
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Déconnexion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <TenantContextBanner />
      <TenantTerminationBanner />

      <div className="flex">
        {/* Sidebar - desktop only */}
        <aside className="hidden w-64 shrink-0 border-r border-border/60 bg-card transition-all duration-200 md:block" data-test="sidebar">
          <Sidebar slug={orgSlug} className="h-[calc(100vh-4rem)]" />
        </aside>

        {/* Main content */}
        <main className="min-w-0 flex-1 animate-fade-in p-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />
      <AIChatWidget />
    </div>
    </EntityCompletenessProvider>
  )
}
